// src/features/analytics/useAnalyticsRange.ts

import { useMemo } from 'react';
import { analyticsRouteApi } from './routeApi';
import { useAnalyticsMeta } from './useAnalyticsMeta';
import {
  resolveAnalyticsRange,
  resolveSelectedAnalyticsRange,
  type AnalyticsSearch,
} from './searchParams';
import type { UtcRange } from '@/shared/utils/utcRange';

export interface UseAnalyticsRangeResult {
  search: AnalyticsSearch;
  /** Floored fetch range — what queries send (the gateway rejects pre-window). */
  range: UtcRange;
  /**
   * What the customer actually selected (no product-window floor). Differs
   * from `range` only when a preset reaches back before the window; the
   * coverage notice names THIS span so a clamped "Last 90 days" still says so.
   */
  selectedRange: UtcRange;
  /** UTC midnight of T when configured (Phase 6); null through Phase 5. */
  productWindowFloorSeconds: number | null;
}

/**
 * Read the validated /analytics search params and resolve them to concrete
 * end-exclusive UTC epoch-second ranges (floored fetch range + unfloored
 * selected range).
 */
export function useAnalyticsRange(): UseAnalyticsRangeResult {
  const search = analyticsRouteApi.useSearch();
  const { productWindowFloorSeconds } = useAnalyticsMeta();

  const { range, selectedRange } = useMemo(() => {
    const nowSeconds = Math.floor(Date.now() / 1000);
    return {
      range: resolveAnalyticsRange(search, nowSeconds, productWindowFloorSeconds),
      selectedRange: resolveSelectedAnalyticsRange(search, nowSeconds),
    };
  }, [search, productWindowFloorSeconds]);

  return { search, range, selectedRange, productWindowFloorSeconds };
}
